import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { WorkspaceButton, type WorkspaceButtonVariant } from '@/components/ui/workspace/button';
import { VI_STEP_PATH } from '@/components/vendor-intelligence/vi-logic';
import type { OverviewDestination } from './overview-plan';

/** The page's own actions a destination can name. */
export interface OverviewCallbacks {
  onEditDetails: () => void;
  onCapture: () => void;
  onArchive: () => void;
}

/**
 * Where an Overview row sends the reader: a link to the screen where the
 * work is done, or one of the page's own actions (the details modal, the
 * snapshot capture, the archive). `button` draws it as a workspace button;
 * without it, a text link with the caller's class.
 */
export function OverviewGo({
  engagementId,
  go,
  callbacks,
  button,
  className,
  children,
}: {
  engagementId: string;
  go: OverviewDestination;
  callbacks?: OverviewCallbacks;
  button?: WorkspaceButtonVariant;
  className?: string;
  children: React.ReactNode;
}) {
  const params = { engagementId };
  const onClick =
    go.to === 'edit-details'
      ? callbacks?.onEditDetails
      : go.to === 'capture'
        ? callbacks?.onCapture
        : go.to === 'archive'
          ? callbacks?.onArchive
          : undefined;
  if (go.to === 'edit-details' || go.to === 'capture' || go.to === 'archive') {
    return button ? (
      <WorkspaceButton variant={button} className={className} onClick={onClick}>
        {children}
      </WorkspaceButton>
    ) : (
      <button type="button" className={className} onClick={onClick}>
        {children}
      </button>
    );
  }

  const linkClass = button ? undefined : className;
  let link: React.ReactElement;
  switch (go.to) {
    case 'discovery-setup':
      link = (
        <Link to="/engagements/$engagementId/discovery/setup" params={params} className={linkClass}>
          {children}
        </Link>
      );
      break;
    case 'sessions':
      link = (
        <Link
          to="/engagements/$engagementId/discovery/sessions"
          params={params}
          search={{
            ...(go.review ? { review: go.review } : {}),
            ...(go.person ? { person: go.person } : {}),
          }}
          className={linkClass}
        >
          {children}
        </Link>
      );
      break;
    case 'findings':
      link = (
        <Link
          to="/engagements/$engagementId/discovery/findings"
          params={params}
          search={{
            ...(go.area ? { area: go.area } : {}),
            ...(go.status ? { status: go.status } : {}),
          }}
          className={linkClass}
        >
          {children}
        </Link>
      );
      break;
    case 'maps':
      link = (
        <Link to="/engagements/$engagementId/discovery/maps" params={params} className={linkClass}>
          {children}
        </Link>
      );
      break;
    case 'signoff':
      link = (
        <Link
          to="/engagements/$engagementId/discovery/signoff"
          params={params}
          className={linkClass}
        >
          {children}
        </Link>
      );
      break;
    case 'selection':
      link = (
        <Link
          to="/engagements/$engagementId/selection"
          params={params}
          search={{
            ...(go.criticality ? { criticality: go.criticality } : {}),
            ...(go.add ? { add: go.add } : {}),
          }}
          className={linkClass}
        >
          {children}
        </Link>
      );
      break;
    case 'vendor-intelligence':
      link = (
        <Link
          to={VI_STEP_PATH}
          params={{ engagementId, step: go.step }}
          search={{}}
          className={linkClass}
        >
          {children}
        </Link>
      );
      break;
  }
  return button ? (
    <WorkspaceButton asChild variant={button} className={className}>
      {link}
    </WorkspaceButton>
  ) : (
    link
  );
}

/** The text link style of the Overview's small links (the Discovery and Selection cards, the stage track). */
export const OVERVIEW_LINK =
  'text-meta font-medium text-ink-700 underline decoration-ink-300 underline-offset-2 hover:text-ink-900 hover:decoration-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 rounded-sm';
